import React, { Suspense, useEffect, useState } from 'react';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';

// Layouts
import AuthLayout from './layout/AuthLayout';
import DashboardLayout from './layout/DashboardLayout';
import usePermission from '../utils/customeHooks/usePermission';
import { ModuleKey } from '../constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentModule, setCurrentPillar } from '../redux/reducers/adminSlice';
import {
  authRoutes,
  ccRoutes,
  dashboardRoutes,
  livelihoodRoutes,
  researchRoutes,
  superAdminRoutes,
  tvRoutes,
} from '../constants/routeData';
import ErrorPage from '../pages/FallBackPages';
const LAST_VISITED_ROUTE_KEY = 'lastVisitedRoute';

const AppRouter = () => {
  const { getModulePermission } = usePermission();
  const location = useLocation();
  const dispatch = useDispatch();
  const { fromResearch } = useSelector((state) => state?.researchData || {});
  const sessionActive = useSelector((state) => state?.auth?.sessionActive);
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const isAuthenticated = Boolean(sessionActive && accessToken);
  const [showOfflineScreen, setShowOfflineScreen] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );

  useEffect(() => {
    const goOffline = () => setShowOfflineScreen(true);
    const goOnline = () => setShowOfflineScreen(false);

    // Native browser connectivity events
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);

    // Custom event from axios service layer (fires after real connectivity check)
    window.addEventListener('app-offline', goOffline);

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('app-offline', goOffline);
    };
  }, []);

  // When offline screen is shown, periodically check if connectivity is restored.
  // This is needed because on Windows with virtual adapters (Hyper-V, WSL),
  // the native 'online' event may not fire when WiFi reconnects.
  useEffect(() => {
    if (!showOfflineScreen) return;

    const checkRecovery = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        await fetch('https://www.google.com/generate_204', {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        setShowOfflineScreen(false);
      } catch {
        // Still offline, keep showing the screen
      }
    };

    const intervalId = setInterval(checkRecovery, 5000);

    return () => clearInterval(intervalId);
  }, [showOfflineScreen]);

  useEffect(() => {
    const excludedPaths = new Set([
      '/',
      '/login',
      '/forgotPassword',
      '/verify-code',
      '/reset-password',
      '/privacy-policy',
      '/no-internet',
    ]);

    if (isAuthenticated && !excludedPaths.has(location.pathname)) {
      localStorage.setItem(
        LAST_VISITED_ROUTE_KEY,
        `${location.pathname}${location.search || ''}${location.hash || ''}`
      );
    }
  }, [isAuthenticated, location.pathname, location.search, location.hash]);

  const allRoutes = [
    ...dashboardRoutes,
    ...tvRoutes,
    ...ccRoutes,
    ...superAdminRoutes,
    ...livelihoodRoutes,
    ...researchRoutes,
  ];

  const sortedRoutes = [...allRoutes].sort((a, b) => {
    return b.path.split('/').length - a.path.split('/').length;
  });

  const currentRoute = sortedRoutes.find((route) =>
    matchPath({ path: route.path, end: true }, location.pathname)
  );
  const lastVisitedRoute = localStorage.getItem(LAST_VISITED_ROUTE_KEY);

  const pillar = fromResearch ? 'research' : currentRoute?.pillar;
  const module = currentRoute?.module;

  useEffect(() => {
    dispatch(setCurrentPillar(pillar || null));
    dispatch(setCurrentModule(module || null));
  }, [pillar, module, dispatch]);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route
          path='/'
          element={
            <Navigate to={isAuthenticated ? lastVisitedRoute || '/dashboard' : '/login'} replace />
          }
        />
        <Route path='/no-internet' element={<ErrorPage type='noInternet' align='center' />} />

        {authRoutes.map(({ path, component: Component }) => (
          <Route
            key={path}
            path={path}
            element={
              isAuthenticated ? (
                <Navigate to={lastVisitedRoute || '/dashboard'} replace />
              ) : (
                <AuthLayout>
                  {showOfflineScreen ? (
                    <ErrorPage type='noInternet' align='center' />
                  ) : (
                    <Component />
                  )}
                </AuthLayout>
              )
            }
          />
        ))}

        {allRoutes.map(
          ({
            path,
            component: Component,
            pillar: routePillar,
            module: routeModule,
            viewScreen: viewScreen,
            isAllow = false,
            byPassResearch = false,
            requiredPermission,
          }) => {
            // Convert module name to backend key using ModuleKey
            const backendModuleKey = routeModule ? ModuleKey[routeModule] || routeModule : null;

            // Determine permission based on navigation source
            let hasPermission;

            if (isAllow) {
              hasPermission = true;
            } else if (byPassResearch && fromResearch) {
              // If coming from Research pillar, bypass permission check
              hasPermission = true;
            } else if (routePillar && backendModuleKey) {
              hasPermission = getModulePermission(
                routePillar,
                backendModuleKey,
                requiredPermission
              );
            } else {
              hasPermission = true;
            }

            let pageContent;
            if (showOfflineScreen) {
              pageContent = <ErrorPage type='noInternet' align='center' />;
            } else if (hasPermission) {
              pageContent = <Component />;
            } else {
              pageContent = <ErrorPage type='noPermission' align='center' />;
            }

            return (
              <Route
                key={path}
                path={path}
                element={
                  isAuthenticated ? (
                    <DashboardLayout>{pageContent}</DashboardLayout>
                  ) : (
                    <Navigate to='/login' replace state={{ from: location }} />
                  )
                }
              />
            );
          }
        )}

        <Route path='*' element={<ErrorPage type='notFound' align='center' />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
